import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Plus } from 'lucide-react';
import { capaApi, Capa, CapaStats } from '../api/capa.api';
import { useAuth } from '@contexts/AuthContext';

const statusColors: Record<string, string> = {
  open: 'bg-red-100 text-red-800',
  assigned: 'bg-yellow-100 text-yellow-800',
  in_progress: 'bg-blue-100 text-blue-800',
  resolved: 'bg-purple-100 text-purple-800',
  verified_closed: 'bg-green-100 text-green-800',
};

const NEXT_STATUS: Record<string, string> = {
  open: 'assigned',
  assigned: 'in_progress',
  in_progress: 'resolved',
  resolved: 'verified_closed',
};

const CapaList: React.FC = () => {
  const { user } = useAuth();
  const [capas, setCapas] = useState<Capa[]>([]);
  const [stats, setStats] = useState<CapaStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [statusModal, setStatusModal] = useState<Capa | null>(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [form, setForm] = useState({ title: '', corrective_action: '', due_date: '' });

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const [list, statsRes] = await Promise.all([
        capaApi.list({ ...(statusFilter ? { status: statusFilter } : {}), limit: 50 }),
        capaApi.getStats(user?.company_id ?? undefined),
      ]);
      setCapas((list as any).capas || []);
      setStats(statsRes);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, user?.company_id]);

  useEffect(() => { load(); }, [load]);

  const handleStatusUpdate = async () => {
    if (!statusModal) return;
    setSubmitting(true);
    try {
      await capaApi.updateStatus(statusModal.id, NEXT_STATUS[statusModal.status] || 'resolved', notes);
      setStatusModal(null);
      setNotes('');
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreate = async () => {
    if (!form.title.trim()) return;
    setSubmitting(true);
    try {
      await capaApi.create({ ...form, company_id: user?.company_id ?? undefined } as any);
      setCreateModal(false);
      setForm({ title: '', corrective_action: '', due_date: '' });
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Corrective Action Plans (CAPA)</h1>
        <div className="flex gap-2">
          <button onClick={load} className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">
            <RefreshCw size={16} /> Refresh
          </button>
          <button onClick={() => setCreateModal(true)} className="flex items-center gap-1 px-3 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700">
            <Plus size={16} /> New CAPA
          </button>
        </div>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: 'Total', value: stats.total, color: 'text-gray-600' },
            { label: 'Open', value: stats.open, color: 'text-red-600' },
            { label: 'In Progress', value: stats.in_progress, color: 'text-blue-600' },
            { label: 'Resolved', value: stats.resolved, color: 'text-purple-600' },
            { label: 'Overdue', value: stats.overdue, color: 'text-red-700' },
          ].map(({ label, value, color }) => (
            <div key={label} className="bg-white border rounded-xl p-4">
              <p className="text-sm text-gray-500 mb-1">{label}</p>
              <p className={`text-2xl font-bold ${color}`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-3">
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
          {['', 'open', 'assigned', 'in_progress', 'resolved', 'verified_closed'].map(s => (
            <option key={s} value={s}>{s ? s.replace('_', ' ') : 'All Statuses'}</option>
          ))}
        </select>
      </div>

      <div className="bg-white border rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
        ) : capas.length === 0 ? (
          <div className="py-12 text-gray-500">No CAPAs found.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                {['Title', 'Responsible', 'Due Date', 'Status', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              {capas.map(capa => (
                <tr key={capa.id}>
                  <td className="px-4 py-3 text-left">
                    <p className="font-medium text-gray-900">{capa.title}</p>
                    {capa.rejection_reason && <p className="text-xs text-gray-500 mt-0.5">Reason: {capa.rejection_reason}</p>}
                  </td>
                  <td className="px-4 py-3 text-left text-gray-600">{capa.responsible_name || capa.responsible_user_id || 'Unassigned'}</td>
                  <td className="px-4 py-3 text-left text-gray-600">{capa.due_date ? new Date(capa.due_date).toLocaleDateString() : '-'}</td>
                  <td className="px-4 py-3 text-left">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[capa.status]}`}>
                      {capa.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-left">
                    {capa.status !== 'verified_closed' && (
                      <button onClick={() => { setStatusModal(capa); setNotes(''); }}
                        className="text-blue-600 hover:underline text-xs">
                        Advance → {NEXT_STATUS[capa.status] || ''}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {statusModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md space-y-4">
            <h2 className="text-lg font-semibold">Advance CAPA Status</h2>
            <p className="text-sm text-gray-600">{statusModal.title}</p>
            <p className="text-sm">New status: <span className="font-medium">{NEXT_STATUS[statusModal.status]?.replace('_', ' ')}</span></p>
            <textarea placeholder="Notes (optional)" value={notes} onChange={e => setNotes(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm" rows={3} />
            <div className="flex gap-3 justify-end">
              <button onClick={() => setStatusModal(null)} className="px-4 py-2 text-sm border rounded-lg">Cancel</button>
              <button onClick={handleStatusUpdate} disabled={submitting}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {submitting ? 'Saving...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {createModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md space-y-4">
            <h2 className="text-lg font-semibold">New CAPA</h2>
            <input placeholder="Title *" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm" />
            <textarea placeholder="Corrective Action" value={form.corrective_action} onChange={e => setForm({ ...form, corrective_action: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm" rows={3} />
            <input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm" />
            <div className="flex gap-3 justify-end">
              <button onClick={() => setCreateModal(false)} className="px-4 py-2 text-sm border rounded-lg">Cancel</button>
              <button onClick={handleCreate} disabled={submitting || !form.title.trim()}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {submitting ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CapaList;
